import TrackPopup from './TrackPopup';
import TrackPointPopup from './TrackPointPopup';
import WaypointPopup from './WaypointPopup';
import AreaPopup from './AreaPopup';
import nearestPointOnLine from '@turf/nearest-point-on-line';
import getSyncTrackMultiLine from '../../../utils/gaia/getSyncTrackMultiLine';
import convertLngLat from '../../../utils/mapbox/convertLngLat';
import {TrackDispatcher} from '../../../hooks/useTracks';
import InteractionSpecPopup from './InteractionSpecPopup';
import React from 'react';
import {WaypointDispatcher} from '../../../hooks/useWaypoints';
import {GetSyncPromise} from '../../../utils/react/useGetSyncPromise';
import {MapboxPopup} from '../../../utils/mapbox/Mapbox';
import OSMPopup from './OsmPopup';
import {AreaDispatcher} from '../../../hooks/useAreas';

interface GetPopupOptions {
    popup: MapPopup;
    areas: Record<string, Area>;
    tracks: Record<string, Track>;
    waypoints: Record<string, Waypoint>;
    waypointDispatcher: WaypointDispatcher;
    trackDispatcher: TrackDispatcher;
    areaDispatcher: AreaDispatcher;
    onPopupChange: (popup: MapPopup) => void;
    onOverlaysChange: SetState<MapOverlays>;
    mapboxStyle: GaiaMapboxStyle | undefined;
    overlays: MapOverlays;
    onWaypointMoveStart: (waypoint: Waypoint, mapboxLayerId: string | undefined) => void;
    onTrackMoveStart: (trackLocalId: string, mapboxLayerId: string | undefined) => void;
    bounds: mapboxgl.LngLatBounds | undefined;
    project: MapProject;
    interaction: MapInteraction;
    getSyncPromise: GetSyncPromise;
    className: string;
}

function getPoint(
    popup: PointPopup,
    tracks: Record<string, Track>,
    waypoints: Record<string, Waypoint>
) {
    if (popup.trackLocalId) {
        const track = tracks[popup.trackLocalId];
        if (!track.isGeometryLoaded) return;
        return track.points[popup.pointLocalId];
    } else {
        return waypoints[popup.pointLocalId];
    }
}

export default function getPopup({
    popup,
    tracks,
    waypoints,
    areas,
    waypointDispatcher,
    trackDispatcher,
    areaDispatcher,
    onPopupChange,
    onOverlaysChange,
    overlays,
    onWaypointMoveStart,
    onTrackMoveStart,
    bounds,
    project,
    interaction,
    mapboxStyle,
    className,
    getSyncPromise
}: GetPopupOptions): MapboxPopup | undefined {
    if (!popup || !mapboxStyle || !mapboxStyle.layers) return undefined;
    if (
        // mapboxLayerId is undefined for popups which are not associated with a layer
        popup.mapboxLayerId !== undefined &&
        !(mapboxStyle.layers.find(layer => layer.id === popup.mapboxLayerId))
    ) return undefined;

    if (popup.type === 'point') {
        const point = getPoint(popup, tracks, waypoints);
        if (!point) return undefined;
        if (point.properties.isLocallyDeleted) return undefined;

        if (point.properties.attr === 'route') {
            return {
                className,
                offset: getPopupOffset({height: 14, width: 14}),
                lngLat: convertLngLat(point.geometry.coordinates),
                children: <TrackPointPopup
                    point={point as TrackPoint}
                    trackDispatcher={trackDispatcher}
                    onPopupChange={onPopupChange}
                    onOverlaysChange={onOverlaysChange}
                    overlays={overlays}
                />
            };
        } else {
            return {
                className,
                lngLat: convertLngLat(point.geometry.coordinates),
                offset: getPopupOffset({height: 32 * 1.25, width: 24 * 1.25}),
                children: <WaypointPopup
                    onPopupChange={onPopupChange}
                    waypoint={point}
                    waypointDispatcher={waypointDispatcher}
                    onWaypointMoveStart={onWaypointMoveStart}
                    onTrackMoveStart={onTrackMoveStart}
                    interaction={interaction}
                    mapboxLayerId={popup.mapboxLayerId}
                />
            };
        }

    } else if (popup.type === 'track') {
        const track = tracks[popup.trackLocalId];
        if (!track) return undefined;
        if (track.properties.isLocallyDeleted) return undefined;

        let lngLat;
        if (popup.lngLat) {
            lngLat = popup.lngLat;

        } else if (bounds) {
            const [multiline] = getSyncTrackMultiLine({track, project, getSyncPromise});
            const point = nearestPointOnLine(multiline.geometry, bounds.getCenter().toArray());
            if (!point || !point.geometry || point.geometry.coordinates[0] === Infinity) return;
            lngLat = convertLngLat(point.geometry.coordinates);
            onPopupChange({...popup, lngLat});

        } else {
            return undefined;
        }

        return {
            className,
            lngLat,
            children: <TrackPopup
                project={project}
                interaction={interaction}
                onPopupChange={onPopupChange}
                trackDispatcher={trackDispatcher}
                tracks={tracks}
                trackLocalId={popup.trackLocalId}
                onTrackMoveStart={onTrackMoveStart}
                mapboxLayerId={popup.mapboxLayerId}
            />
        };

    } else if (popup.type === 'area') {
        const area = areas[popup.areaLocalId];
        if (area.properties.isLocallyDeleted || !popup.lngLat) return undefined;

        return {
            className,
            lngLat: popup.lngLat,
            children: <AreaPopup
                onPopupChange={onPopupChange}
                areaDispatcher={areaDispatcher}
                areas={areas}
                areaLocalId={popup.areaLocalId}
            />
        };

    } else if (popup.type === 'interactionSpec') {
        return {
            className,
            lngLat: popup.lngLat,
            children: <InteractionSpecPopup
                feature={popup.feature}
                mapboxStyle={mapboxStyle}
                onPopupChange={onPopupChange}
            />
        };
    } else if (popup.type === 'osm') {
        return {
            className,
            lngLat: popup.lngLat,
            children: <OSMPopup
                title={popup.title}
                osmId={popup.osmId}
                onPopupChange={onPopupChange}
            />
        };
    }
}

function getPopupOffset({height, width}: {height: number; width: number}) {
    return {
        top: [0, 0] as [number, number],
        'top-left': [0, 0] as [number, number],
        'top-right': [0, 0] as [number, number],
        bottom: [0, -(height)] as [number, number],
        'bottom-left': [0, -(height)] as [number, number],
        'bottom-right': [0, -(height)] as [number, number],
        right: [-(width / 2), -(height / 2)] as [number, number],
        left: [width / 2, -(height / 2)] as [number, number],
    };
}
